import React, { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Drawer, Input, Spin } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { Colors } from 'context';
import { Button, ButtonSizes, CheckHomeworkButton, ToastTypes } from 'atoms';
import { HomeworkGradesType } from 'types';
import { parseDate } from 'services/DateService';
import {
  HomeworkErrorCode,
  useGradeHomework,
  useHomeworkDownloadUrl,
  useRerunHomeworkAiReview,
  useToaster,
} from 'core';
import type { IHomeworkRosterRow } from 'core';
import { getHomeworkErrorText } from 'organisms/HomeworkSubmit';

export interface IHomeworkReviewDrawer {
  open: boolean;
  lessonId: string;
  /** The roster row being reviewed; null while closed. */
  row: IHomeworkRosterRow | null;
  /** Server-side HOMEWORK_AI_ENABLED — the AI section is hidden when off. */
  aiEnabled: boolean;
  onClose: () => void;
}

const GRADE_OPTIONS = [
  HomeworkGradesType.DONE,
  HomeworkGradesType.CONTAINS_ERRORS,
  HomeworkGradesType.NOT_DONE,
];

/**
 * Teacher's review drawer (spec 0012 §6): the student's work (GitHub link or
 * presigned ZIP download), the staff-only AI verdict card with a re-run
 * button, and grading (existing 3-level hw_mark + comment) via gradeHomework.
 * Grading works for non-submitters too (they get "not done").
 */
export const HomeworkReviewDrawer = memo(
  ({ open, lessonId, row, aiEnabled, onClose }: IHomeworkReviewDrawer) => {
    const { t } = useTranslation();
    const { addToast } = useToaster();
    const { gradeHomework, loading: gradeLoading } = useGradeHomework();
    const { rerunAiReview, loading: rerunLoading } = useRerunHomeworkAiReview();
    const { getDownloadUrl, loading: downloadLoading } = useHomeworkDownloadUrl();

    const [grade, setGrade] = useState<HomeworkGradesType | null>(null);
    const [comment, setComment] = useState('');

    const studentId = row?.studentId;
    const submission = row?.submission ?? null;
    const aiReview = submission?.aiReview ?? null;

    // Re-seed the local grading state when another student is opened.
    useEffect(() => {
      setGrade(row?.hwMark ?? null);
      setComment(row?.submission?.teacherComment || '');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studentId, open]);

    const onDownload = useCallback(async () => {
      if (!submission) {
        return;
      }
      try {
        const url = await getDownloadUrl(submission.id);
        if (url) {
          window.open(url, '_blank', 'noopener');
        } else {
          addToast('homework_download', {
            type: ToastTypes.danger,
            text: `${t('homework.drawer.downloadError')}`,
          });
        }
      } catch {
        addToast('homework_download', {
          type: ToastTypes.danger,
          text: `${t('homework.drawer.downloadError')}`,
        });
      }
    }, [submission, getDownloadUrl, addToast, t]);

    const onRerun = useCallback(async () => {
      if (!submission) {
        return;
      }
      try {
        const result = await rerunAiReview(submission.id);
        if (result.success) {
          addToast('homework_ai_rerun', {
            type: ToastTypes.success,
            text: `${t('homework.drawer.ai.rerunStarted')}`,
          });
        } else if (result.errorCode === HomeworkErrorCode.AI_DISABLED) {
          addToast('homework_ai_rerun', {
            type: ToastTypes.warning,
            text: `${t('homework.errors.aiDisabled')}`,
          });
        } else {
          addToast('homework_ai_rerun', {
            type: ToastTypes.danger,
            text: getHomeworkErrorText(t, result.errorCode),
          });
        }
      } catch {
        addToast('homework_ai_rerun', {
          type: ToastTypes.danger,
          text: `${t('homework.errors.generic')}`,
        });
      }
    }, [submission, rerunAiReview, addToast, t]);

    const onSaveGrade = useCallback(async () => {
      if (!row || !grade) {
        return;
      }
      try {
        const result = await gradeHomework(
          lessonId,
          row.studentId,
          grade,
          // Отправляем всегда: для несдавшего сервер заводит строку-держатель,
          // которая и хранит вердикт.
          comment.trim(),
          // Pin the attempt this drawer is showing: if the student re-submitted
          // meanwhile, the server answers STALE_ROSTER instead of grading work
          // the teacher never opened.
          submission?.attempt ?? null,
        );
        if (result.success) {
          addToast('homework_grade', {
            type: ToastTypes.success,
            text: `${t('homework.drawer.saveSuccess')}`,
          });
          onClose();
        } else if (result.errorCode === HomeworkErrorCode.STALE_ROSTER) {
          addToast('homework_grade', {
            type: ToastTypes.warning,
            text: `${t('homework.errors.staleRoster')}`,
          });
          onClose(); // the roster refetch already fired; reopen on fresh data
        } else {
          addToast('homework_grade', {
            type: ToastTypes.danger,
            text: getHomeworkErrorText(t, result.errorCode),
          });
        }
      } catch {
        addToast('homework_grade', {
          type: ToastTypes.danger,
          text: `${t('homework.drawer.saveError')}`,
        });
      }
    }, [row, grade, comment, submission, lessonId, gradeHomework, addToast, onClose, t]);

    return (
      <Drawer open={open} onClose={onClose} title={row?.studentName} width={480}>
        {row && (
          <>
            <SectionTitle>{t('homework.drawer.workTitle')}</SectionTitle>
            {submission ? (
              <Section>
                <MetaText>
                  {t('homework.submit.attempt', { attempt: submission.attempt })} ·{' '}
                  {parseDate(submission.submittedAt)}
                </MetaText>
                {/* rev.3: link AND file can both be present — render each
                    independently (download stays per-submission, one file). */}
                {submission.githubUrl && (
                  <WorkLink href={submission.githubUrl} target="_blank" rel="noopener noreferrer">
                    {submission.githubUrl}
                  </WorkLink>
                )}
                {submission.fileName && (
                  <>
                    <MetaText>{submission.fileName}</MetaText>
                    <Button
                      variant="outline"
                      size={ButtonSizes.sm}
                      colors={{ borderColor: Colors.gray }}
                      isLoading={downloadLoading}
                      disabled={downloadLoading}
                      onClick={onDownload}>
                      {t('homework.drawer.downloadZip')}
                    </Button>
                  </>
                )}
                {submission.isGraded && submission.gradedByName && (
                  <MetaText>
                    {t('homework.drawer.gradedBy', { name: submission.gradedByName })}
                  </MetaText>
                )}
              </Section>
            ) : (
              <Section>
                <MetaText>{t('homework.status.notSubmitted')}</MetaText>
              </Section>
            )}

            {submission && aiEnabled && (
              <>
                <SectionTitle>{t('homework.drawer.ai.title')}</SectionTitle>
                <Section>
                  {aiReview ? (
                    <>
                      {aiReview.isInProgress && (
                        <SpinRow>
                          <Spin size="small" />
                          <MetaText>{t(`homework.drawer.ai.${aiReview.status}`)}</MetaText>
                        </SpinRow>
                      )}
                      {aiReview.isFailed && (
                        <AlertStyled
                          type="error"
                          showIcon
                          message={`${t('homework.drawer.ai.failed')}`}
                          description={aiReview.errorMessage || undefined}
                        />
                      )}
                      {aiReview.isDone && (
                        <>
                          {aiReview.aiScore !== null && (
                            <ScoreText>
                              {t('homework.drawer.ai.score')}: {aiReview.aiScore}/100
                            </ScoreText>
                          )}
                          {aiReview.aiSummary && (
                            <>
                              <FieldLabel>{t('homework.drawer.ai.summary')}</FieldLabel>
                              <BodyText>{aiReview.aiSummary}</BodyText>
                            </>
                          )}
                          {aiReview.aiJustification && (
                            <>
                              <FieldLabel>{t('homework.drawer.ai.justification')}</FieldLabel>
                              <BodyText>{aiReview.aiJustification}</BodyText>
                            </>
                          )}
                        </>
                      )}
                      {aiReview.suspiciousContent && (
                        <AlertStyled
                          type="warning"
                          showIcon
                          message={`${t('homework.drawer.ai.suspicious')}`}
                          description={aiReview.suspicionNotes || undefined}
                        />
                      )}
                      {/* The verdict is only as good as what the fetcher could
                          read — say so instead of letting a partial basis pass
                          for the whole submission. */}
                      {aiReview.isDone && aiReview.truncatedBasis && (
                        <AlertStyled
                          type="info"
                          showIcon
                          message={`${t('homework.drawer.ai.truncatedBasis', {
                            n: aiReview.filesUsedCount ?? 0,
                          })}`}
                        />
                      )}
                      {aiReview.isDone && aiReview.assignmentSource && (
                        <MetaText>
                          {t('homework.drawer.ai.assignmentSource')}:{' '}
                          {t(`homework.drawer.ai.source.${aiReview.assignmentSource}`, {
                            defaultValue: aiReview.assignmentSource,
                          })}
                        </MetaText>
                      )}
                      <RerunButton
                        variant="outline"
                        size={ButtonSizes.sm}
                        colors={{ borderColor: Colors.gray }}
                        isLoading={rerunLoading}
                        disabled={rerunLoading || aiReview.isInProgress}
                        onClick={onRerun}>
                        {t('homework.drawer.ai.rerun')}
                      </RerunButton>
                    </>
                  ) : (
                    // No review row = submitted before the AI flag was on. The
                    // rerun mutation creates the row, so the button must be
                    // reachable here too.
                    <>
                      <MetaText>{t('homework.drawer.ai.neverRun')}</MetaText>
                      <RerunButton
                        variant="outline"
                        size={ButtonSizes.sm}
                        colors={{ borderColor: Colors.gray }}
                        isLoading={rerunLoading}
                        disabled={rerunLoading}
                        onClick={onRerun}>
                        {t('homework.drawer.ai.rerun')}
                      </RerunButton>
                    </>
                  )}
                </Section>
              </>
            )}

            <SectionTitle>{t('homework.drawer.gradeLabel')}</SectionTitle>
            <Section>
              <GradesRow>
                {GRADE_OPTIONS.map((gradeOption) => (
                  <CheckHomeworkButton
                    key={gradeOption}
                    gradeType={gradeOption}
                    isActive={grade === gradeOption}
                    onClick={setGrade}
                  />
                ))}
              </GradesRow>
              {/* Комментарий доступен ВСЕГДА, в том числе несдавшему: сервер
                  заводит для вердикта строку-держатель. Раньше поле здесь
                  пряталось, потому что комментарий физически негде было
                  хранить. */}
              <Input.TextArea
                rows={4}
                value={comment}
                placeholder={`${t('homework.drawer.commentPlaceholder')}`}
                onChange={(event) => setComment(event.target.value)}
              />
              <SaveButton
                isLoading={gradeLoading}
                disabled={!grade || gradeLoading}
                onClick={onSaveGrade}>
                {t('homework.drawer.save')}
              </SaveButton>
            </Section>
          </>
        )}
      </Drawer>
    );
  },
);

const SectionTitle = styled.h4`
  margin: 0 0 8px;
  ${fontService.getFont({ size: 16, lineHeight: 150, weight: FontWeight.bold })}
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SpinRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetaText = styled.p`
  margin: 0 0 8px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  word-break: break-all;
`;

const WorkLink = styled.a`
  display: inline-block;
  margin: 0 0 8px;
  word-break: break-all;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
`;

const ScoreText = styled.p`
  margin: 0 0 8px;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.bold })}
`;

const FieldLabel = styled.p`
  margin: 8px 0 4px;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.medium })}
`;

const BodyText = styled.p`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
`;

const AlertStyled = styled(Alert)`
  margin: 8px 0;
`;

const RerunButton = styled(Button)`
  margin-top: 12px;
`;

const GradesRow = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const SaveButton = styled(Button)`
  margin-top: 16px;
`;
